import type { Socket } from 'socket.io-client';
import type { CaseData } from '~/types/caseData';
import { CompletionStatusColor } from '~/types/caseData';

export type BlockData = {
  id?: string;
  moduleId?: string;
  name: string;
  status: string;
  entries?: BlockDataEntry[];
  canStart?: boolean;
  completed?: boolean;
  casesNumber?: number;
  cases: CaseData[];
  approximateDuration?: number;
  coverImage?: string;
  timeExtension?: number;
  showTimer?: boolean;
  order?: number;
  module?: {
    name: string;
  };
  caseTeachingVideoEmbed?: string;
  supplementalTeachingVideoEmbed?: string;
  teachingMaterials?: TeachingMaterialEntry[];
  title?: string;
  userStartedBlocks?: StartedBlockData[];
};

export type BlockDataState = BlockData & {
  timeline_started?: boolean;
  timeline_startFrom?: number;
  refresh?: boolean;
  hardRefresh?: boolean;
  patientsConsultLoaded?: boolean;
  createdStartedBlock?: any;
  showLoading?: boolean;
  redirectTo?: string;
};

/* TO DO: this will replace BlockDataState, need to delete that one after this update is complete */
export type BlockDataGeneral = {
  block?: BlockData;
  entries?: BlockDataEntry[];
  isBlockStarted?: boolean;
  isBlockCompleted?: boolean;
  blockStarted?: StartedBlockData;
  liveRoom?: boolean;
  roomCode?: string;
  socket?: Socket;
  countdown: string;
  timeline?: {
    started: boolean;
    startFrom: number;
  };
  dataLoaded: boolean;
};

export type Timeline = {
  type: string;
  timerValue?: number;
  data?: any;
  visible: boolean;
};

export type StartedBlockData = {
  id?: string;
  userStartedCaseStudies: CaseData[];
  timeSpent?: number;
  completed?: boolean;
};

export type TeachingMaterialEntry = {
  id: string;
  title: string;
  thumbnailPicture?: string;
  thumbnailPictureName?: string;
  media?: string;
  mediaName?: string;
  signedMedia?: string;
  signedThumbnailPicture?: string;
};

export type PromptData = {
  prompt: string;
  name: string;
  media: string;
  signedMedia?: string;
  distractor_id: string;
  options: {
    option_1: string;
    option_2: string;
    option_3: string;
  };
};

export type BlockDataEntry =
  | {
      type: 'case';
      timer: number;
      timerFullFormat?: { minutes: number; seconds: number };
      edit: boolean;
      data?: CaseData;
    }
  | {
      type: 'prompt';
      timer: number;
      timerFullFormat?: { minutes: number; seconds: number };
      edit: boolean;
      data: PromptData;
    };

export const casesFromBlock = (block: BlockData, startedCaseData?: CaseData[]) => {
  if (!block.entries) return [];
  const cases: CaseData[] = [];

  block.entries.forEach((entry) => {
    if (entry.type === 'case') {
      const caseData = entry.data as CaseData;
      caseData.room = caseData.roomNumber;
      if (!cases.some((existingCase) => existingCase.id === caseData.id)) {
        cases.push(caseData);
      }

      const startedCaseStudy = startedCaseData?.find(
        (caseStudy) => caseStudy.caseStudyId === caseData.id
      );

      caseData.completed = startedCaseStudy?.finished ?? false;
      const isActive = Boolean(startedCaseStudy);
      if (caseData.completed) {
        caseData.completionStatus = CompletionStatusColor.COMPLETED;
      } else if (isActive) {
        caseData.completionStatus = CompletionStatusColor.ACTIVE;
      } else {
        caseData.completionStatus = CompletionStatusColor.WAITING;
      }

      caseData.score = startedCaseStudy?.score ?? 0;
    }
  });
  return cases;
};
